/**
 * NotesSection Component
 * Private advisor notes for session management
 */

import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/shared/lib/api";
import { Card, CardHeader, CardTitle, CardContent, Button } from "@/shared/components/ui";
import { StickyNote, Save, Loader2, CheckCircle } from "lucide-react";

interface NotesSectionProps {
  sessionId: string;
  initialNotes?: string;
}

export function NotesSection({ sessionId, initialNotes = "" }: NotesSectionProps) {
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState(initialNotes);
  const [savedNotes, setSavedNotes] = useState(initialNotes);
  const [showSaved, setShowSaved] = useState(false);

  // Keep local state in sync with props
  useEffect(() => {
    setNotes(initialNotes);
    setSavedNotes(initialNotes);
  }, [initialNotes]);

  const hasChanges = notes !== savedNotes;

  const saveMutation = useMutation({
    mutationFn: () => api.updateSessionNotes(sessionId, notes),
    onSuccess: () => {
      setSavedNotes(notes);
      queryClient.invalidateQueries({ queryKey: ["session", sessionId] });
      // Show saved indicator
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 2000);
    },
  });

  const handleSave = () => {
    if (hasChanges) {
      saveMutation.mutate();
    }
  };

  // Auto-save on blur if there are changes
  const handleBlur = () => {
    if (hasChanges && !saveMutation.isPending) {
      saveMutation.mutate();
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="flex items-center gap-2">
          <StickyNote className="w-5 h-5" />
          Notatki prywatne
        </CardTitle>
        <div className="flex items-center gap-2">
          {showSaved && (
            <span className="text-sm text-success-600 flex items-center gap-1">
              <CheckCircle className="w-4 h-4" />
              Zapisano
            </span>
          )}
          {hasChanges && (
            <Button
              size="sm"
              variant={saveMutation.isPending ? "secondary" : "primary"}
              onClick={handleSave}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  Zapisuję...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-1" />
                  Zapisz
                </>
              )}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={handleBlur}
            placeholder="Dodaj prywatne notatki dotyczące tej sesji... (widoczne tylko dla Ciebie)"
            rows={4}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-y focus:ring-2 focus:ring-primary-500 focus:border-primary-500 placeholder:text-gray-400"
          />
          <p className="text-xs text-gray-500">
            Te notatki są prywatne i nie są widoczne dla klienta. Zmiany zapisują się automatycznie.
          </p>
        </div>

        {saveMutation.error && (
          <div className="mt-2 p-2 bg-error-50 border border-error-200 rounded text-sm text-error-700">
            Nie udało się zapisać notatek. Spróbuj ponownie.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
